/** ADR-0001 — xxhash64 over the canonical encoding.
 *
 * BigInt is used here and only here: hashing is not state, and BigInt gives an
 * exact 64-bit result on every runtime, which is what I12 needs.
 */

const M = (1n << 64n) - 1n;
const P1 = 0x9e3779b185ebca87n;
const P2 = 0xc2b2ae3d27d4eb4fn;
const P3 = 0x165667b19e3779f9n;
const P4 = 0x85ebca77c2b2ae63n;
const P5 = 0x27d4eb2f165667c5n;

const mul = (a: bigint, b: bigint): bigint => (a * b) & M;
const add = (a: bigint, b: bigint): bigint => (a + b) & M;
const rotl = (v: bigint, r: bigint): bigint => ((v << r) | (v >> (64n - r))) & M;

const round = (acc: bigint, input: bigint): bigint => mul(rotl(add(acc, mul(input, P2)), 31n), P1);

function mergeRound(acc: bigint, val: bigint): bigint {
  const v = round(0n, val);
  return add(mul((acc ^ v) & M, P1), P4);
}

export function xxhash64(data: Uint8Array, seed = 0n): bigint {
  const len = data.length;
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  let h: bigint;
  let i = 0;

  if (len >= 32) {
    let v1 = add(add(seed, P1), P2);
    let v2 = add(seed, P2);
    let v3 = seed;
    let v4 = (seed - P1) & M;
    const limit = len - 32;
    do {
      v1 = round(v1, view.getBigUint64(i, true)); i += 8;
      v2 = round(v2, view.getBigUint64(i, true)); i += 8;
      v3 = round(v3, view.getBigUint64(i, true)); i += 8;
      v4 = round(v4, view.getBigUint64(i, true)); i += 8;
    } while (i <= limit);
    h = (rotl(v1, 1n) + rotl(v2, 7n) + rotl(v3, 12n) + rotl(v4, 18n)) & M;
    h = mergeRound(h, v1);
    h = mergeRound(h, v2);
    h = mergeRound(h, v3);
    h = mergeRound(h, v4);
  } else {
    h = add(seed, P5);
  }

  h = add(h, BigInt(len));

  while (i + 8 <= len) {
    h = add(mul(rotl((h ^ round(0n, view.getBigUint64(i, true))) & M, 27n), P1), P4);
    i += 8;
  }
  if (i + 4 <= len) {
    h = add(mul(rotl((h ^ mul(BigInt(view.getUint32(i, true)), P1)) & M, 23n), P2), P3);
    i += 4;
  }
  while (i < len) {
    h = mul(rotl((h ^ mul(BigInt(data[i]!), P5)) & M, 11n), P1);
    i += 1;
  }

  h = (h ^ (h >> 33n)) & M;
  h = mul(h, P2);
  h = (h ^ (h >> 29n)) & M;
  h = mul(h, P3);
  h = (h ^ (h >> 32n)) & M;
  return h;
}

const enc = new TextEncoder();

export function hashString(s: string): string {
  return xxhash64(enc.encode(s)).toString(16).padStart(16, "0");
}
